import {validateImageFile} from './validateImage';
import {enqueueSnackbar} from 'notistack';

const isLocalHost = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

const getFileExtension = (mimeType: string) => {
  if (mimeType === 'image/jpeg') return 'jpg';
  if (mimeType === 'image/png') return 'png';
  return 'png';
};

const ensureUploadFile = (file: File | Blob, field: string) => {
  if (file instanceof File) {
    return file;
  }

  const type = file.type || 'image/png';
  const ext = getFileExtension(type);
  return new File([file], `${field}-${Date.now()}.${ext}`, {
    type,
    lastModified: Date.now(),
  });
};

const toStoredMediaPath = (value: string) => {
  let result = value.trim().replace(/\\/g, '/');
  if (isLocalHost()) {
    result = result.replace(/^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?/i, '');
    result = result.replace(/^\/+/, '');
    result = result.replace(/^storage\/app\/public\//i, '');
    result = result.replace(/^app\/public\//i, '');
    result = result.replace(/^storage\//i, '');
  }
  return result;
};

const toPreviewMediaPath = (value: string, localPreview = false) => {
  let result = value.trim().replace(/\\/g, '/');
  if (/^(blob:|data:|https?:)/i.test(result)) {
    return result;
  }
  result = result.replace(/^\/+/, '');
  if (result.startsWith('storage/app/public/')) {
    const relativePath = result.slice('storage/app/public/'.length);
    return localPreview ? `/media/${relativePath}` : `/storage/${relativePath}`;
  }
  if (result.startsWith('app/public/')) {
    const relativePath = result.slice('app/public/'.length);
    return localPreview ? `/media/${relativePath}` : `/storage/${relativePath}`;
  }
  if (result.startsWith('storage/')) {
    const relativePath = result.slice('storage/'.length);
    return localPreview ? `/media/${relativePath}` : `/${result}`;
  }
  if (result.startsWith('logo/') || result.startsWith('marketing_image/')) {
    return localPreview ? `/media/${result}` : `/storage/${result}`;
  }
  return value.startsWith('/') ? value : `/${result}`;
};

const getUploadErrorMessage = (error: any) => {
  const apiErrors = error?.data?.errors;
  const details = apiErrors && typeof apiErrors === 'object'
    ? Object.values(apiErrors)
      .flat()
      .filter(Boolean)
      .join(' ')
    : '';

  if (details) {
    return details;
  }

  return error?.data?.message || 'Ошибка загрузки файла';
};

export const uploadImage = async ({
  file,
  type,
  uploadFile,
  handleFormChange,
  formState,
  field,
  persistPath = false, // для edit
}: {
  file: File | Blob;
  type: string;
  uploadFile: any;
  handleFormChange: any;
  formState: any;
  field: string;
  persistPath?: boolean;
}) => {
  const uploadTargetFile = ensureUploadFile(file, field);
  const error = await validateImageFile(uploadTargetFile);
  if (error) {
    enqueueSnackbar(error, {variant: 'error'});
    if (error) throw new Error(error);
  }

  // добавляем File в стейт с функциональным обновлением (чтобы не терять предыдущие)
  handleFormChange(field, (prev: any[] = []) => [...prev, uploadTargetFile]);

  try {
    const form = new FormData();
    form.append('file', uploadTargetFile);
    form.append('type', type);

    const payload = await uploadFile(form).unwrap();

    // заменяем File на путь, добавляя путь в конец. Очищаем временные File-объекты.
    const normalizedPath = toStoredMediaPath(String(payload.path || ''));
    if (!normalizedPath.trim()) {
      throw new Error('Ошибка загрузки файла');
    }
    handleFormChange(field, (prev: any[] = []) => {
      const onlyStrings = prev.filter(item => typeof item === 'string');
      return [...onlyStrings, normalizedPath];
    });
    return normalizedPath;
  } catch (error: any) {
    // Удаляем файл из стейта при ошибке, чтобы не оставлять мусор
    handleFormChange(field, (prev: any[] = []) => prev.filter(item => item !== uploadTargetFile));

    enqueueSnackbar(getUploadErrorMessage(error), {
      variant: 'error',
      autoHideDuration: 5000,
      style: {maxWidth: '700px'},
      anchorOrigin: {horizontal: 'right', vertical: 'top'},
    });
    throw error;
  }
};

export const cropImageToAspect = async (file: File, aspect = 1): Promise<File> => {
  const imageUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Не удалось обработать изображение'));
      img.src = imageUrl;
    });

    const imageAspect = image.width / image.height;
    let cropWidth = image.width;
    let cropHeight = image.height;
    let offsetX = 0;
    let offsetY = 0;

    if (imageAspect > aspect) {
      cropWidth = Math.round(image.height * aspect);
      offsetX = Math.round((image.width - cropWidth) / 2);
    } else if (imageAspect < aspect) {
      cropHeight = Math.round(image.width / aspect);
      offsetY = Math.round((image.height - cropHeight) / 2);
    }

    const canvas = document.createElement('canvas');
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Не удалось подготовить canvas');
    }

    context.drawImage(
      image,
      offsetX,
      offsetY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    );

    const outputType = file.type === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    const croppedBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Не удалось сформировать изображение'));
            return;
          }
          resolve(blob);
        },
        outputType,
        outputType === 'image/jpeg' ? 0.95 : undefined,
      );
    });

    const nextName = file.name.replace(/\.[^.]+$/, outputType === 'image/jpeg' ? '.jpg' : '.png');
    return new File([croppedBlob], nextName, {
      type: outputType,
      lastModified: Date.now(),
    });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

export const getPreviewUrl = (item: File | string | Blob) => {
  if (item instanceof File || item instanceof Blob) {
    return URL.createObjectURL(item);
  }
  if (typeof item === 'string') {
    if (/^(blob:|data:)/i.test(item)) {
      return item;
    }
    if (/^https?:/i.test(item)) {
      try {
        const url = new URL(item);
        if (isLocalHost() && (url.hostname === 'localhost' || url.hostname === '127.0.0.1')) {
          url.pathname = toPreviewMediaPath(url.pathname, true);
          return url.toString();
        }
        return item;
      } catch {
        return toPreviewMediaPath(item, isLocalHost());
      }
    }
    if (item) {
      const normalizedItem = toPreviewMediaPath(item, isLocalHost());
      const rawBase = (import.meta as any).env?.VITE_BACKEND_URL || (import.meta as any).env?.VITE_API_URL || '';
      const base = String(rawBase).replace(/\/+$/, '');
      if (!base) return normalizedItem;
      return `${base}${normalizedItem.startsWith('/') ? '' : '/'}${normalizedItem}`;
    }
  }
  return item as string;
};
